import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  createDocumentTemplate,
  deleteDocumentTemplate,
  downloadDocumentTemplate,
  getDocumentTemplateFieldReference,
  getDocumentTemplates,
  updateDocumentTemplate,
} from "../api/documentTemplatesApi";
import { getApiErrorMessage } from "../utils/concurrency";

const TEMPLATE_TYPES = [
  { value: "service_charge_invoice", label: "Service charge invoice" },
  { value: "account_statement", label: "Account statement" },
  { value: "payment_receipt", label: "Payment receipt" },
];

const emptyForm = {
  id: null,
  name: "",
  template_type: "service_charge_invoice",
  file: null,
  is_active: false,
};

export default function DocumentTemplatesSettingsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [fileLabel, setFileLabel] = useState("");
  const [fieldReference, setFieldReference] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await getDocumentTemplates();
      setRows(res.data.results || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load document templates."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const loadReference = async () => {
      try {
        const res = await getDocumentTemplateFieldReference(form.template_type);
        setFieldReference(res.data);
      } catch (e) {
        setFieldReference(null);
      }
    };
    if (dialogOpen) {
      loadReference();
    }
  }, [dialogOpen, form.template_type]);

  const activeByType = useMemo(() => {
    const map = {};
    rows.forEach((row) => {
      if (row.is_active) map[row.template_type] = row;
    });
    return map;
  }, [rows]);

  const openCreate = () => {
    setForm(emptyForm);
    setFileLabel("");
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name,
      template_type: row.template_type,
      file: null,
      is_active: row.is_active,
    });
    setFileLabel(row.file_name || "");
    setDialogOpen(true);
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("name", form.name.trim());
    if (!form.id) {
      formData.append("template_type", form.template_type);
    }
    formData.append("is_active", form.is_active ? "true" : "false");
    if (form.file) {
      formData.append("file", form.file);
    }
    return formData;
  };

  const save = async () => {
    if (!form.name.trim()) {
      setError("Template name is required.");
      return;
    }
    if (!form.id && !form.file) {
      setError("A Word .docx template file is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const formData = buildFormData();
      if (form.id) {
        await updateDocumentTemplate(form.id, formData);
        setToast({ open: true, message: "Template updated.", severity: "success" });
      } else {
        await createDocumentTemplate(formData);
        setToast({ open: true, message: "Template created.", severity: "success" });
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to save template."));
    } finally {
      setSaving(false);
    }
  };

  const activateTemplate = async (row) => {
    try {
      setSaving(true);
      setError("");
      const formData = new FormData();
      formData.append("is_active", "true");
      await updateDocumentTemplate(row.id, formData);
      setToast({ open: true, message: `${row.name} is now active.`, severity: "success" });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to activate template."));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (row) => {
    try {
      const res = await downloadDocumentTemplate(row.id);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = row.file_name || "template.docx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to download template."));
    }
  };

  const confirmDelete = async () => {
    if (!templateToDelete) return;
    try {
      setSaving(true);
      setError("");
      await deleteDocumentTemplate(templateToDelete.id);
      setToast({ open: true, message: "Template deleted.", severity: "success" });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete template."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage Word document templates used to generate PDFs for owners. Templates use
        Microsoft Word mail-merge fields. Download a template, edit it in Word, then
        upload a new version here.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" onClick={openCreate}>
          Add template
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>File</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5}>Loading…</TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No templates yet.</TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.template_type_label}</TableCell>
                <TableCell>{row.file_name}</TableCell>
                <TableCell>
                  {row.is_active ? (
                    <Chip size="small" color="success" label="Active" />
                  ) : (
                    <Chip size="small" label="Inactive" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => handleDownload(row)}>
                    Download
                  </Button>
                  <Button size="small" onClick={() => openEdit(row)}>
                    Edit
                  </Button>
                  {!row.is_active && (
                    <Button size="small" onClick={() => activateTemplate(row)} disabled={saving}>
                      Activate
                    </Button>
                  )}
                  {!row.is_active && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        setTemplateToDelete(row);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Active templates
          </Typography>
          {TEMPLATE_TYPES.map((type) => (
            <Typography key={type.value} variant="body2" color="text.secondary">
              {type.label}: {activeByType[type.value]?.name || "None"}
            </Typography>
          ))}
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{form.id ? "Edit template" : "Add template"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Template name"
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          {!form.id && (
            <FormControl fullWidth margin="normal">
              <InputLabel>Template type</InputLabel>
              <Select
                label="Template type"
                value={form.template_type}
                onChange={(e) => setForm((prev) => ({ ...prev, template_type: e.target.value }))}
              >
                {TEMPLATE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" component="label">
              {form.id ? "Replace Word file" : "Upload Word file"}
              <input
                hidden
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setForm((prev) => ({ ...prev, file }));
                  setFileLabel(file?.name || "");
                }}
              />
            </Button>
            {fileLabel && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {fileLabel}
              </Typography>
            )}
          </Box>
          {!form.id && (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
              }
              label="Set as active template for this type"
            />
          )}
          {fieldReference && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Available merge fields
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {fieldReference.description}
              </Typography>
              <Typography variant="body2" component="div">
                {(fieldReference.fields || []).map((field) => (
                  <div key={field.name}>
                    <strong>{field.name}</strong> — {field.description}
                  </div>
                ))}
              </Typography>
              {(fieldReference.repeating_regions || []).map((region) => (
                <Box key={region.name} sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Repeating region: {region.name}
                  </Typography>
                  {(region.fields || []).map((field) => (
                    <Typography key={field.name} variant="body2" component="div">
                      {field.name} — {field.description}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete template?</DialogTitle>
        <DialogContent>
          <Typography>
            Delete &quot;{templateToDelete?.name}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
